import {
  getDictationRefusal,
  type FinalDocumentRevision,
  type RecordingStopReason,
} from '@/shared/api/transcriptor';
import { transcriptorApi } from '@/shared/api/transcriptor/transcriptorApi';
import { debug } from '@/shared/lib';

import { AsyncBlockingQueue } from './AsyncBlockingQueue';
import { AudioChunkPayload, RecordingDiagnostic } from './types';

const log = (...args: unknown[]) => debug('[ChunkUploader]', ...args);

const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;

const NON_RETRYABLE_STATUS_CODES = new Set([400, 401, 403, 404, 409]);

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const isNonRetryableError = (error: unknown): boolean => {
  if (error instanceof Response) {
    return NON_RETRYABLE_STATUS_CODES.has(error.status);
  }

  if (error && typeof error === 'object' && 'status' in error) {
    return NON_RETRYABLE_STATUS_CODES.has((error as { status: number }).status);
  }

  const message = String(error);

  return NON_RETRYABLE_STATUS_CODES.has(
    Number(message.match(/\b(400|401|403|404|409)\b/)?.[1]),
  );
};

const getRetryDelay = (attempt: number): number =>
  Math.min(INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt), MAX_RETRY_DELAY_MS);

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  const binary = Array.from(bytes)
    .map((byte) => String.fromCharCode(byte))
    .join('');
  return btoa(binary);
};

export type ChunkUploaderCallbacks = {
  onUploadIssue?: (hasIssue: boolean) => void;
  onError?: (error: string) => void;
  onStopped?: (stopReason: RecordingStopReason) => void;
  /** Fires once per chunk the backend acknowledged; skipped empty chunks don't count. */
  onChunkUploaded?: (uploadedAt: string, chunkIndex: number) => void;
  onDiagnostic?: (diagnostic: RecordingDiagnostic) => void;
  /** Awaited before /stop: what must reach the server before the recording stops. */
  beforeStop?: () => Promise<FinalDocumentRevision[] | void>;
};

// Exit reasons help us decide what to do after the upload loop ends.
// We only call /stop endpoint if we successfully processed the last chunk.
type ExitReason = 'last_chunk' | 'fatal_error' | 'aborted';

/**
 * Robust chunk uploader with buffered queue and infinite retry.
 * Ensures all chunks are uploaded before calling stop endpoint.
 */
export class ChunkUploader {
  private readonly consultationId: string;
  private readonly fragmentId: string;
  private readonly queue: AsyncBlockingQueue<AudioChunkPayload> =
    new AsyncBlockingQueue();
  private isRunning = false;
  private disposed = false;
  private hasIssue = false;
  private callbacks: ChunkUploaderCallbacks = {};
  // Track why we exited the loop to decide if we should call /stop
  private exitReason: ExitReason | null = null;
  // Carried from the last chunk to /stop so the backend records why the
  // recording ended. A last chunk without a reason is the user's Finish.
  private stopReason: RecordingStopReason = 'user';

  constructor(consultationId: string, fragmentId: string) {
    this.consultationId = consultationId;
    this.fragmentId = fragmentId;
  }

  setCallbacks(callbacks: ChunkUploaderCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Add a chunk to the upload queue.
   */
  addChunk(chunk: AudioChunkPayload): void {
    if (chunk.isLast && chunk.stopReason) {
      this.stopReason = chunk.stopReason;
    }
    this.queue.enqueue(chunk);
  }

  /**
   * Start the upload worker loop.
   * Processes chunks sequentially with infinite retry on failure.
   */
  start(): void {
    if (this.isRunning) {
      return;
    }
    this.isRunning = true;
    this.exitReason = null;
    this.runUploadLoop();
  }

  /**
   * Stop the uploader gracefully.
   * Aborts the queue to unblock any pending dequeue() calls.
   */
  dispose(): void {
    this.disposed = true;
    this.isRunning = false;
    // Abort queue to unblock the upload loop if it's waiting for chunks.
    // Without this, the loop would hang forever on await queue.dequeue().
    this.queue.abort();
  }

  isActive(): boolean {
    return this.isRunning;
  }

  private async runUploadLoop(): Promise<void> {
    while (this.isRunning) {
      let chunk: AudioChunkPayload;

      try {
        chunk = await this.queue.dequeue();
      } catch {
        // Queue was aborted - this is expected when dispose() is called
        this.exitReason = 'aborted';
        break;
      }

      try {
        await this.uploadChunkWithRetry(chunk);
      } catch (error) {
        // Fatal error - stop uploading
        log('fatal error, stopping', error);
        this.exitReason = 'fatal_error';
        this.callbacks.onError?.(String(error));
        this.isRunning = false;
        break;
      }

      if (chunk.isLast) {
        this.exitReason = 'last_chunk';
        this.isRunning = false;
        break;
      }
    }

    // Only call /stop if we successfully uploaded the last chunk.
    // Don't call it on abort or fatal error - the recording is incomplete.
    if (this.exitReason === 'last_chunk') {
      // The final revisions only let scribe skip its own completion of a
      // dictation, so failing to get them does not keep the recording open.
      const finalRevisions = await this.callbacks
        .beforeStop?.()
        .catch((error: unknown) => {
          log('stopping without final revisions', error);
          return undefined;
        });
      if (this.disposed) {
        return;
      }
      try {
        await this.callStopEndpoint(finalRevisions || undefined);
        this.callbacks.onStopped?.(this.stopReason);
      } catch (error) {
        log('failed to call stop endpoint', error);
        this.callbacks.onError?.(`Failed to stop recording: ${error}`);
      }
    }
  }

  private async uploadChunkWithRetry(chunk: AudioChunkPayload): Promise<void> {
    let attempt = 0;

    // Retry loop - continues until upload succeeds or a non-retryable error occurs
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        await this.uploadChunk(chunk);

        // Success - clear issue flag and exit loop
        if (this.hasIssue) {
          this.hasIssue = false;
          this.callbacks.onUploadIssue?.(false);
        }

        return;
      } catch (error) {
        // Scribe completes a dictation from its live transcript, so audio it
        // refuses past the limit is not needed and the recording goes on to
        // its Stop.
        if (getDictationRefusal(error) === 'dictation_limit_reached') {
          log('Chunk refused past the dictation limit', { index: chunk.index });
          return;
        }

        if (isNonRetryableError(error)) {
          log('Non-retryable error, stopping', { index: chunk.index, error });
          throw error;
        }

        attempt++;
        const delay = getRetryDelay(attempt);

        log('Intermittent error, retrying', {
          index: chunk.index,
          attempt,
          nextDelayMs: delay,
          error,
        });
        this.callbacks.onDiagnostic?.({
          name: 'chunk_upload_retry',
          attributes: {
            chunk_index: chunk.index,
            attempt,
            error: String(error),
          },
        });

        // Set issue flag to notify UI of upload problems
        if (!this.hasIssue) {
          this.hasIssue = true;
          this.callbacks.onUploadIssue?.(true);
        }

        await sleep(delay);

        // Check if we should still be running (dispose was called during sleep)
        if (!this.isRunning) {
          throw new Error('Uploader stopped during retry');
        }
      }
    }
  }

  private async uploadChunk(chunk: AudioChunkPayload): Promise<void> {
    // Skip empty non-final chunks, but always send the last chunk
    // so the backend knows we're done (even if it has no audio data)
    if (chunk.audioData.size === 0 && !chunk.isLast) {
      return;
    }

    const base64 =
      chunk.audioData.size > 0 ? await blobToBase64(chunk.audioData) : '';

    const response = await transcriptorApi.addAudioChunk(
      this.consultationId,
      this.fragmentId,
      {
        audio_base64: base64,
        type: chunk.audioData.type || 'audio/webm',
        timestamp: new Date().toISOString(),
        index: chunk.index,
        last: chunk.isLast,
      },
    );

    if (response.error) {
      throw new Error(response.error);
    }

    this.callbacks.onChunkUploaded?.(new Date().toISOString(), chunk.index);
  }

  private async callStopEndpoint(
    finalRevisions?: FinalDocumentRevision[],
  ): Promise<void> {
    const response = await transcriptorApi.stopRecording(
      this.consultationId,
      this.fragmentId,
      {
        reason: this.stopReason,
        ...(finalRevisions ? { final_document_revisions: finalRevisions } : {}),
      },
    );

    if (response.error) {
      throw new Error(response.error);
    }
  }
}
